/**
 * sync.js — Gmail/Calendar sync helpers. All config reads/writes go through
 * the BigQuery backend (/api/config). No Sheets reads.
 */

export const BACKEND_BASE_URL = import.meta.env.VITE_BACKEND_BASE_URL ?? 'https://sales-dashboard-backend-842367857924.us-west2.run.app';

async function _apiFetch(getToken, path, options = {}) {
  const token = await getToken();
  const res = await fetch(`${BACKEND_BASE_URL}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(options.headers ?? {}),
    },
  });
  if (!res.ok) {
    let message = res.statusText;
    try { message = (await res.json())?.error ?? message; } catch (_) {}
    throw new Error(message);
  }
  return res.json();
}

export async function fetchSyncUsers(getToken) {
  const data = await _apiFetch(getToken, '/api/sync-users', { method: 'GET' });
  return Array.isArray(data?.users) ? data.users : [];
}

export async function checkConnected(getToken) {
  const data = await _apiFetch(getToken, '/api/me/connected', { method: 'GET' });
  return !!data?.connected;
}

/**
 * startOAuthConnect — exchanges the caller's live GIS access token for a
 * short-lived, single-use code via a normal (Authorization-header) fetch,
 * then returns the URL to navigate to. The browser redirect that follows
 * can't set headers, so this keeps the real, fully-scoped access token out
 * of the URL — only the one-time code (dead after ~3 minutes or first use)
 * ever appears there.
 */
export async function startOAuthConnect(getToken, redirect) {
  const { code } = await _apiFetch(getToken, '/api/oauth/connect/start', {
    method: 'POST',
    body: JSON.stringify({ redirect }),
  });
  return `${BACKEND_BASE_URL}/api/oauth/connect?code=${encodeURIComponent(code)}`;
}

export async function requestBackendGmailSync(getToken, users = []) {
  return _apiFetch(getToken, '/api/sync-gmail', {
    method: 'POST',
    body: JSON.stringify({ users }),
  });
}

export async function fetchGmailSyncStatus(getToken) {
  return _apiFetch(getToken, '/api/sync-gmail/status', { method: 'GET' });
}

/**
 * pollGmailSyncStatus — kick off a Gmail sync and poll /api/sync-gmail/status
 * until it finishes. The sync now runs in the background on the server (it can
 * take minutes), so we no longer hold a single long request open — that was
 * dropping the connection and surfacing as "load failed".
 *
 * Resolves when the sync completes, rejects on server-reported error or timeout.
 */
export async function pollGmailSyncStatus(getToken, users = [], {
  intervalMs = 3000,
  timeoutMs  = 25 * 60 * 1000,
} = {}) {
  await requestBackendGmailSync(getToken, users);

  const deadline = Date.now() + timeoutMs;
  // Give the background worker a moment to flip status to 'running' before we
  // start trusting a not-running response as "done".
  await new Promise((r) => setTimeout(r, intervalMs));

  while (Date.now() < deadline) {
    let st;
    try {
      st = await fetchGmailSyncStatus(getToken);
    } catch (_) {
      // transient network/error — keep polling
      await new Promise((r) => setTimeout(r, intervalMs));
      continue;
    }
    if (st?.error) throw new Error(st.error);
    if (!st?.running && (st?.status === 'ok' || st?.lastSyncAt)) return st;
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  throw new Error('Sync timed out — it may still be running in the background');
}

export async function requestBackendCalendarSync(getToken, users = []) {
  return _apiFetch(getToken, '/api/sync-calendar', {
    method: 'POST',
    body: JSON.stringify({ users }),
  });
}

export async function fetchCalendarSyncStatus(getToken) {
  return _apiFetch(getToken, '/api/sync-calendar/status', { method: 'GET' });
}

/**
 * pollCalendarSyncStatus — kick off a calendar sync and poll until done.
 * Returns the final status object; throws on error or timeout.
 */
export async function pollCalendarSyncStatus(getToken, users = [], {
  intervalMs = 3000,
  timeoutMs  = 10 * 60 * 1000,
} = {}) {
  await requestBackendCalendarSync(getToken, users);

  const deadline = Date.now() + timeoutMs;
  await new Promise((r) => setTimeout(r, intervalMs));

  while (Date.now() < deadline) {
    let st;
    try {
      st = await fetchCalendarSyncStatus(getToken);
    } catch (_) {
      await new Promise((r) => setTimeout(r, intervalMs));
      continue;
    }
    if (st?.error) throw new Error(st.error);
    if (!st?.running && (st?.status === 'ok' || st?.status?.startsWith('reconnect:') || st?.lastSyncAt)) return st;
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  throw new Error('Calendar sync timed out — it may still be running in the background');
}

export async function requestAmplitudeSync(getToken) {
  return _apiFetch(getToken, '/api/sync-amplitude', { method: 'POST' });
}

/**
 * pollForSync — polls /api/config until configKey has a timestamp newer than
 * `before`, then calls onDone(). Uses the BQ config table, not Sheets.
 *
 * Returns a cancel() function.
 */
export function pollForSync(getToken, configKey, before, {
  onDone,
  onError,
  onTimeout,
  statusKey,
  intervalMs = 1500,
  timeoutMs  = 120000,
} = {}) {
  const deadline = Date.now() + timeoutMs;
  let cancelled  = false;

  const id = setInterval(async () => {
    if (cancelled) { clearInterval(id); return; }

    if (Date.now() >= deadline) {
      clearInterval(id);
      onTimeout?.();
      return;
    }

    try {
      const config = await _apiFetch(getToken, '/api/config');

      if (statusKey) {
        const status = String(config[statusKey] ?? '').trim();
        if (status && status.toLowerCase().startsWith('error')) {
          clearInterval(id);
          onError?.(status.replace(/^error:\s*/i, ''));
          return;
        }
      }

      const val = config[configKey];
      if (val) {
        const ts = new Date(val).getTime();
        if (!isNaN(ts) && ts > before) {
          clearInterval(id);
          onDone?.();
        }
      }
    } catch (_) {
      // transient error — keep polling
    }
  }, intervalMs);

  return () => { cancelled = true; clearInterval(id); };
}
